import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import OnboardingLayout from './OnboardingLayout';
import { getClinicDetails, setClinicDetails } from '../../Utils/clinicAuth';
import { updateClinicProfile } from '../../Services/APIs/authentication';
import { useState } from 'react';

const PHONE_REGEX = /^[0-9+\-\s()]+$/;
const WEBSITE_REGEX = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(\/\S*)?$/i;

const errorClasses = "text-xs text-cv2-destructive mt-1";

/* Same color-mix() approach as the other v2 pages — Tailwind's opacity modifier doesn't work on
 * our var(--cv2-*)-based custom colors. */
const SCOPED_STYLES = `
  .cv2-practise-eyebrow { color: color-mix(in srgb, var(--cv2-foreground) 28%, transparent); }
  .cv2-practise-subcopy { color: color-mix(in srgb, var(--cv2-foreground) 38%, transparent); }
  .cv2-practise-row { border-color: color-mix(in srgb, var(--cv2-foreground) 7%, transparent); }
  .cv2-practise-label { color: color-mix(in srgb, var(--cv2-foreground) 30%, transparent); }
  .cv2-practise-input::placeholder { color: color-mix(in srgb, var(--cv2-foreground) 18%, transparent); }
  .cv2-practise-error-banner {
    color: var(--cv2-destructive);
    background-color: color-mix(in srgb, var(--cv2-destructive) 10%, transparent);
    border-color: color-mix(in srgb, var(--cv2-destructive) 30%, transparent);
  }
`;

const validationSchema = Yup.object().shape({
    practise_name: Yup.string()
        .trim()
        .min(2, 'Practise name must be at least 2 characters')
        .max(100, 'Practise name must be at most 100 characters')
        .required('Practise name is required'),
    phone_number: Yup.string()
        .matches(PHONE_REGEX, 'Enter a valid phone number')
        .min(10, 'Phone number must be at least 10 characters')
        .max(20, 'Phone number must be at most 20 characters')
        .required('Phone number is required'),
    address: Yup.string()
        .trim()
        .min(5, 'Location must be at least 5 characters')
        .max(500, 'Location must be at most 500 characters')
        .required('Location is required'),
    website: Yup.string().matches(WEBSITE_REGEX, { message: 'Enter a valid website (e.g. example.com)', excludeEmptyString: true }),
});

const LineField = ({ name, label, type = 'text', placeholder }) => (
    <div className="cv2-practise-row border-t pt-[17px] pb-4">
        <label htmlFor={name} className="cv2-practise-label block font-spaceMono text-[9px] tracking-[1.2px] uppercase mb-[7px]">
            {label}
        </label>
        <Field
            type={type}
            name={name}
            placeholder={placeholder}
            className="cv2-practise-input w-full bg-transparent border-0 outline-none font-aeonik text-[15px] text-cv2-foreground normal-case"
        />
        <ErrorMessage name={name} component="div" className={errorClasses} />
    </div>
);

const Practise = ({ onBack, onNext }) => {
    const clinicDetails = getClinicDetails();
    const [submitError, setSubmitError] = useState('');

    const initialValues = {
        practise_name: clinicDetails?.practise_name || '',
        phone_number: clinicDetails?.phone_number || '',
        address: clinicDetails?.address || '',
        website: clinicDetails?.website || '',
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitError('');
        try {
            const response = await updateClinicProfile(values);
            setClinicDetails({ ...clinicDetails, ...(response.data?.data || values) });
            onNext();
        } catch (error) {
            setSubmitError(error?.response?.data?.message || error?.data?.message || 'Something went wrong. Please try again.');
        }
        setSubmitting(false);
    };

    return (
        <OnboardingLayout stepKey="practise" onBack={onBack}>
            <style>{SCOPED_STYLES}</style>
            <div className="flex-1 flex items-center justify-center px-7 sm:px-12 py-[72px]">
                <div className="max-w-[400px] w-full">
                    <div className="mb-11">
                        <p className="cv2-practise-eyebrow font-spaceMono text-[9px] tracking-[1.4px] uppercase mb-4">
                            Step 1 of 2
                        </p>
                        <h2 className="font-aeonik font-normal text-[38px] leading-[44px] tracking-[-0.9px] text-cv2-foreground mb-[10px] normal-case">
                            Your Practice
                        </h2>
                        <p className="cv2-practise-subcopy font-aeonik text-sm leading-[22px] normal-case">
                            These details personalize your dashboard and patient-facing materials.
                        </p>
                    </div>

                    {submitError && (
                        <div className="cv2-practise-error-banner text-sm border rounded-md px-3 py-2 mb-6">
                            {submitError}
                        </div>
                    )}

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ isSubmitting }) => (
                            <Form className="pt-11">
                                <LineField name="practise_name" label="Practice Name" placeholder="Harris Dental" />
                                <LineField name="address" label="Location" placeholder="City, State" />
                                <LineField name="phone_number" label="Phone" type="tel" placeholder="(512) 000-0000" />
                                <LineField name="website" label="Website" placeholder="harrisdental.com" />

                                <div className="cv2-practise-row border-t pt-7">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="inline-flex items-center h-11 px-7 bg-claraview-secondary hover:brightness-110 disabled:opacity-50 transition-all rounded-[3px] font-spaceMono text-[11px] tracking-[1.2px] text-claraview-quaternary uppercase"
                                    >
                                        Save & Continue
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </OnboardingLayout>
    );
};

export default Practise;
